import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/apiAdmin';
import { adminSessionUsable } from '../../utils/authAdmin';
import { useAdminWebSocket } from '../../hooks/useProjectWebSocket';
import Tooltip from '../../components/Tooltip';
import {
    FaPlus,
    FaCopy,
    FaTrash,
    FaPencilAlt,
    FaLayerGroup,
    FaPuzzlePiece,
    FaTimes,
    FaCog,
    FaArrowUp,
    FaArrowDown
} from 'react-icons/fa';

/**
 * ⚠️ DEBOUNCED, AND THIS SURFACE NEEDS IT MOST.
 *
 * `template_updated` is emitted for EVERY task, epic or comment row carrying a
 * `tmpl_` project id, so each save inside an open Template Editor reaches every
 * open Templates-list tab - and this page answers with THREE requests. The
 * routes are cheap (a type-index Query, a config read), so this is request
 * VOLUME rather than table cost, but it is volume multiplied by open tabs.
 *
 * 500ms, matching ProjectDataContext's existing debounce rather than inventing
 * a second interval for the same job.
 *
 * ⛔ BELOW THE LAST IMPORT. It sat between two import statements, which is an
 * `import/first` ERROR under the CRA lint config - and a production
 * `react-scripts build` fails on an ESLint error, so the first push of this
 * plan would have failed at `npm run build` with every test green (Fable,
 * round 3). The test runner does not lint; only the build does.
 */
const LIVE_REFRESH_DEBOUNCE_MS = 500;

const Templates = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showCloneModal, setShowCloneModal] = useState(null);
    const [cloneName, setCloneName] = useState('');
    const [cloneDescription, setCloneDescription] = useState('');
    const [cloneProjectData, setCloneProjectData] = useState(null);
    const [cloneSelectedAddins, setCloneSelectedAddins] = useState([]);
    const [cloneLoadingProject, setCloneLoadingProject] = useState(false);
    const [filter, setFilter] = useState('all');
    const [saving, setSaving] = useState(false);
    const [cloning, setCloning] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);

    // Size management state
    const [sizes, setSizes] = useState([]);
    const [showSizeModal, setShowSizeModal] = useState(false);
    const [editedSizes, setEditedSizes] = useState([]);
    const [sizesSaving, setSizesSaving] = useState(false);

    // Pool add-ins state
    const [poolAddins, setPoolAddins] = useState([]);
    const [poolLoading, setPoolLoading] = useState(false);
    const [poolSizeFilter, setPoolSizeFilter] = useState('');
    const [poolCategoryFilter, setPoolCategoryFilter] = useState('');

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'main',
        abbreviation: '',
        colour: '#3B82F6',
        size: '',
        category: ''
    });

    // ⚠️ SILENT MODE (LIV, D1). These are MOUNT-TIME loaders, and D1 answered
    // "silent refresh". Called as they stand from a message path they would flip
    // a loading state on a page somebody is reading, and put an error banner up
    // for a request the user never made. So the message path passes
    // `{ silent: true }`: no loading flag, no banner, current data kept on
    // failure, and the reason on the console. The mount path is unchanged.
    //
    // The house already names this pattern - ProjectDataContext: "no
    // showLoading parameter either… which is the wanted silent-refresh
    // behaviour".

    // Fetch templates
    const fetchTemplates = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setError(null);
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/templates`);
            if (response.ok) {
                const data = await response.json();
                setTemplates(data.templates || []);
            } else if (!silent) {
                setError('Failed to fetch templates');
            } else {
                console.warn('Background templates refresh failed - keeping the list on screen');
            }
        } catch (err) {
            console.error('Failed to fetch templates:', err);
            if (!silent) setError('Failed to fetch templates');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Fetch size categories
    const fetchSizes = useCallback(async () => {
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/templates/sizes`);
            if (response.ok) {
                const data = await response.json();
                setSizes(data.sizes);
            }
        } catch (err) {
            console.error('Failed to fetch sizes:', err);
        }
    }, []);

    // Fetch pool add-ins
    const fetchPoolAddins = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setPoolLoading(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/templates/pool`);
            if (response.ok) {
                const data = await response.json();
                setPoolAddins(data.pool);
            } else if (silent) {
                console.warn('Background pool refresh failed - keeping the list on screen');
            }
        } catch (err) {
            console.error('Failed to fetch pool add-ins:', err);
        } finally {
            if (!silent) setPoolLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
        fetchSizes();
    }, [fetchTemplates, fetchSizes]);

    // Load pool add-ins data
    useEffect(() => {
        fetchPoolAddins();
    }, [fetchPoolAddins]);

    // -----------------------------------------------------------------------
    // Live updates (LIV-09). No backend change at all: `template_updated` has
    // existed since the templates stream was wired and only TemplateEditor
    // listened for it.
    //
    // ⚠️ THREE refetches, not one. `fetchTemplates` loads the list, `fetchSizes`
    // the size categories the mount effect loads beside it, and
    // `fetchPoolAddins` the pool - which is rows in the SAME templates table, so
    // this signal covers it and omitting the call would leave that list stale
    // while the one above it updated.
    // -----------------------------------------------------------------------

    // GATE 3. The file's own flags, listed - not a description of them. An
    // earlier revision of the plan replaced the prose "an open
    // create/clone/delete confirmation" with five names and lost the three the
    // prose had actually named.
    const liveRefreshBlocked = Boolean(
        showCreateModal || showEditModal || showCloneModal || showDeleteConfirm
        || showSizeModal || saving || cloning || deleting
    );
    const liveRefreshBlockedRef = useRef(liveRefreshBlocked);
    useEffect(() => {
        liveRefreshBlockedRef.current = liveRefreshBlocked;
    }, [liveRefreshBlocked]);

    // D11: a suppressed message is DEFERRED, never dropped. With no pending
    // pill anywhere in this plan, dropping it would leave the list stale until
    // some unrelated change happened to arrive - possibly hours.
    const pendingRefetchRef = useRef(false);
    const refreshTimerRef = useRef(null);

    // GATE 0 lives HERE - at the point of refetch, once per coalesced refresh -
    // not per message in the socket handler (Fable, round 2): there it renewed
    // the session for message types this page ignores, and the deferred flush
    // below reached apiFetch, and its redirect, with no gate at all.
    const liveRefresh = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(async () => {
            refreshTimerRef.current = null;
            // GATE 0 - renew the session if that is all it needs; skip when it
            // cannot be renewed, so a message can never redirect an idle tab.
            if (!await adminSessionUsable()) return;
            fetchTemplates({ silent: true });
            fetchSizes();
            fetchPoolAddins({ silent: true });
        }, LIVE_REFRESH_DEBOUNCE_MS);
    }, [fetchTemplates, fetchSizes, fetchPoolAddins]);

    useEffect(() => () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    }, []);

    // The deferred flush: ten held messages coalesce into one refetch.
    useEffect(() => {
        if (liveRefreshBlocked || !pendingRefetchRef.current) return;
        pendingRefetchRef.current = false;
        liveRefresh();
    }, [liveRefreshBlocked, liveRefresh]);

    useAdminWebSocket(useCallback((message) => {
        // GATE 0 is inside liveRefresh, at the point of refetch.
        // GATE 1 - type.
        if (message.type !== 'template_updated') return;
        // GATE 2 - none: this page lists ALL templates, so any of them is
        // relevant. Deliberately the one surface with no id filter.
        // GATE 3 - do not clobber an open dialog; hold the signal instead.
        if (liveRefreshBlockedRef.current) {
            pendingRefetchRef.current = true;
            return;
        }
        liveRefresh();
    }, [liveRefresh]));

    // Save sizes
    const handleSaveSizes = async () => {
        setSizesSaving(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/templates/sizes`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sizes: editedSizes })
            });
            if (response.ok) {
                setSizes(editedSizes);
                setShowSizeModal(false);
            } else {
                const data = await response.json();
                if (data.error) {
                    setError(data.error);
                } else {
                    setError('Failed to save sizes');
                }
            }
        } catch (err) {
            console.error('Failed to save sizes:', err);
            setError('Failed to save sizes');
        } finally {
            setSizesSaving(false);
        }
    };

    // Size modal helpers
    const openSizeModal = () => {
        setEditedSizes(sizes.length > 0 ? [...sizes] : [
            { id: 1, name: 'Small', order: 1 },
            { id: 2, name: 'Medium', order: 2 },
            { id: 3, name: 'Large', order: 3 }
        ]);
        setShowSizeModal(true);
    };

    const addSize = () => {
        const maxId = editedSizes.reduce((max, s) => Math.max(max, s.id), 0);
        const maxOrder = editedSizes.reduce((max, s) => Math.max(max, s.order), 0);
        setEditedSizes([...editedSizes, { id: maxId + 1, name: '', order: maxOrder + 1 }]);
    };

    const removeSize = (id) => {
        setEditedSizes(editedSizes.filter(s => s.id !== id));
    };

    const renameSize = (id, name) => {
        setEditedSizes(editedSizes.map(s => s.id === id ? { ...s, name } : s));
    };

    const moveSizeUp = (index) => {
        if (index === 0) return;
        const updated = [...editedSizes];
        const temp = updated[index - 1].order;
        updated[index - 1].order = updated[index].order;
        updated[index].order = temp;
        [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
        setEditedSizes(updated);
    };

    const moveSizeDown = (index) => {
        if (index === editedSizes.length - 1) return;
        const updated = [...editedSizes];
        const temp = updated[index + 1].order;
        updated[index + 1].order = updated[index].order;
        updated[index].order = temp;
        [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
        setEditedSizes(updated);
    };

    // Pool filtering
    const filteredPoolAddins = poolAddins.filter(a => {
        if (poolSizeFilter && a.size !== poolSizeFilter) return false;
        if (poolCategoryFilter && a.category !== poolCategoryFilter) return false;
        return true;
    });

    const poolCategories = [...new Set(poolAddins.map(a => a.category).filter(Boolean))].sort();

    // Filtered templates
    const filteredTemplates = templates.filter(t => {
        if (filter === 'all') return true;
        return t.type === filter;
    });

    // Main and add-in templates for split view
    const mainTemplates = templates.filter(t => t.type === 'main');
    const addinTemplates = templates.filter(t => t.type === 'addin');

    // Create template
    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/templates`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            if (response.ok) {
                setShowCreateModal(false);
                setSelectedTemplate(null);
                setFormData({ name: '', description: '', type: 'main', abbreviation: '', colour: '#3B82F6', size: '', category: '' });
                fetchTemplates();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to create template');
            }
        } catch (err) {
            console.error('Failed to create template:', err);
            setError('Failed to create template');
        } finally {
            setSaving(false);
        }
    };

    // Update template
    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedTemplate) return;
        setSaving(true);
        setError(null);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/templates/${selectedTemplate.templateId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                }
            );
            if (response.ok) {
                setShowEditModal(false);
                setSelectedTemplate(null);
                fetchTemplates();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to update template');
            }
        } catch (err) {
            console.error('Failed to update template:', err);
            setError('Failed to update template');
        } finally {
            setSaving(false);
        }
    };

    // Open clone modal and fetch project data for add-in selection
    const openCloneModal = async (template) => {
        setCloneName(`${template.name} (Copy)`);
        setCloneDescription(template.description || '');
        setShowCloneModal(template);
        setCloneProjectData(null);
        setCloneSelectedAddins([]);

        // Only fetch project data for main templates
        if (template.type === 'main') {
            setCloneLoadingProject(true);
            try {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/templates/${template.templateId}/project`
                );
                if (response.ok) {
                    const data = await response.json();
                    setCloneProjectData(data);
                    // Pre-select all attached add-ins by default
                    const attachedAddins = (data.project?.addins || [])
                        .filter(a => a.attached)
                        .map(a => a.addinId);
                    setCloneSelectedAddins(attachedAddins);
                }
            } catch (err) {
                console.error('Failed to fetch project data:', err);
            } finally {
                setCloneLoadingProject(false);
            }
        }
    };

    // Clone template
    const handleClone = async () => {
        if (!showCloneModal || !cloneName.trim() || cloning) return;

        try {
            setCloning(true);
            setError(null);

            // Build request body with optional add-in selection
            const requestBody = {
                name: cloneName.trim(),
                description: cloneDescription.trim()
            };

            // Only pass selectedAddins for main templates with add-ins
            if (showCloneModal.type === 'main' && cloneProjectData?.project?.addins?.length > 0) {
                requestBody.selectedAddins = cloneSelectedAddins;
            }

            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/templates/${showCloneModal.templateId}/clone`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody)
                }
            );
            if (response.ok) {
                setShowCloneModal(null);
                setSelectedTemplate(null);
                setCloneName('');
                setCloneDescription('');
                setCloneProjectData(null);
                setCloneSelectedAddins([]);
                fetchTemplates();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to clone template');
            }
        } catch (err) {
            console.error('Failed to clone template:', err);
            setError('Failed to clone template');
        } finally {
            setCloning(false);
        }
    };

    // Delete template
    const handleDelete = async () => {
        if (!showDeleteConfirm || deleting) return;
        try {
            setDeleting(true);
            setError(null);
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/templates/${showDeleteConfirm.templateId}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                setShowDeleteConfirm(null);
                setSelectedTemplate(null);
                fetchTemplates();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to delete template');
                setShowDeleteConfirm(null);
                setSelectedTemplate(null);
            }
        } catch (err) {
            console.error('Failed to delete template:', err);
            setError('Failed to delete template');
        } finally {
            setDeleting(false);
        }
    };

    // Open edit modal
    const openEditModal = (template) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name,
            description: template.description || '',
            type: template.type,
            abbreviation: template.abbreviation,
            colour: template.colour || '#3B82F6',
            size: template.size != null ? template.size : '',
            category: template.category != null ? template.category : ''
        });
        setShowEditModal(true);
    };

    // Open template editor
    const openEditor = (template) => {
        if (template.type === 'main') {
            navigate(`/admin/templates/${template.templateId}/edit`);
        }
    };

    if (loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="mt-4 text-gray-600">Loading Templates...</p>
            </div>
        );
    }

    // Helper to render a template card
    const renderTemplateCard = (template) => (
        <div
            key={template.templateId}
            onClick={(e) => { document.activeElement.blur(); setSelectedTemplate(template); }}
            className={`bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all cursor-pointer overflow-hidden group border border-gray-200 ${
                selectedTemplate?.templateId === template.templateId
                    ? 'shadow-xl'
                    : ''
            }`}
        >
            {/* Coloured top bar */}
            <div
                className="h-1.5"
                style={{ backgroundColor: template.type === 'addin' ? (template.colour || '#8B5CF6') : '#d1d5db' }}
            />

            <div className="p-5">
                <div className="flex items-start gap-4">
                    {/* Type icon */}
                    <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            template.type === 'main' ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                        style={template.type === 'addin' ? {
                            backgroundColor: `${template.colour || '#8B5CF6'}15`,
                            color: template.colour || '#8B5CF6'
                        } : undefined}
                    >
                        {template.type === 'main' ? (
                            <FaLayerGroup className="w-5 h-5" />
                        ) : (
                            <FaPuzzlePiece className="w-5 h-5" />
                        )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            {/* fixed: the card is overflow-hidden, an absolute
                                popup would clip at its edge */}
                            <Tooltip fixed text={template.name} className="min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate">
                                    {template.name}
                                </h3>
                            </Tooltip>
                            {template.type === 'addin' && template.abbreviation && (
                                <span
                                    className="px-2 py-0.5 text-xs font-bold rounded-md flex-shrink-0"
                                    style={{
                                        backgroundColor: `${template.colour}15`,
                                        color: template.colour
                                    }}
                                >
                                    {template.abbreviation}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                            {template.description || 'No description'}
                        </p>
                        <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                            <span
                                className={`px-2 py-1 rounded-md ${
                                    template.type === 'main' ? 'bg-gray-100 text-gray-600' : ''
                                }`}
                                style={template.type === 'addin' ? {
                                    backgroundColor: `${template.colour || '#8B5CF6'}15`,
                                    color: template.colour || '#8B5CF6'
                                } : undefined}
                            >
                                {template.type === 'main' ? 'Main' : 'Add-in'}
                            </span>
                            {template.size && (
                                <span className="px-2 py-1 rounded-md bg-gray-100 text-gray-600">
                                    {template.size}
                                </span>
                            )}
                            {template.type === 'main' && (template.addins || []).map(a => {
                                const pool = templates.find(t => t.templateId === a.addinId);
                                if (!pool) {
                                    console.error('Attached add-in has no pool template:', a.addinId, 'on', template.templateId);
                                    return null;
                                }
                                return (
                                    <span
                                        key={a.addinId}
                                        title={pool.name}
                                        className="px-2 py-1 text-xs font-bold rounded-md flex-shrink-0"
                                        style={{
                                            backgroundColor: `${pool.colour}15`,
                                            color: pool.colour
                                        }}
                                    >
                                        {pool.abbreviation}
                                    </span>
                                );
                            })}
                            <span>Updated {new Date(template.updatedAt).toLocaleDateString('en-GB')}</span>
                        </div>
                    </div>
                </div>

                {/* Actions - show on hover/select */}
                <div className={`relative z-10 flex items-center justify-end gap-2 mt-4 pt-4 border-t border-gray-100 transition-opacity ${
                    selectedTemplate?.templateId === template.templateId ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}>
                    {template.type === 'main' && (
                        <button
                            onClick={(e) => { e.stopPropagation(); e.currentTarget.blur(); openEditor(template); }}
                            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                        >
                            Open Editor
                        </button>
                    )}
                    <div className="relative group/edit">
                        <button
                            onClick={(e) => { e.stopPropagation(); e.currentTarget.blur(); openEditModal(template); }}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        >
                            <FaPencilAlt className="w-3.5 h-3.5" />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover/edit:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Edit
                        </span>
                    </div>
                    <div className="relative group/clone">
                        <button
                            onClick={(e) => { e.stopPropagation(); e.currentTarget.blur(); openCloneModal(template); }}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        >
                            <FaCopy className="w-3.5 h-3.5" />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover/clone:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Clone
                        </span>
                    </div>
                    <div className="relative group/delete">
                        <button
                            onClick={(e) => { e.stopPropagation(); e.currentTarget.blur(); setShowDeleteConfirm(template); }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                            <FaTrash className="w-3.5 h-3.5" />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover/delete:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Delete
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    // Helper to render a pool add-in card (not directly openable in editor)
    const renderPoolAddinCard = (addin) => (
        <div
            key={addin.templateId}
            className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200"
        >
            <div
                className="h-1.5"
                style={{ backgroundColor: addin.colour }}
            />
            <div className="p-5">
                <div className="flex items-start gap-4">
                    <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{
                            backgroundColor: `${addin.colour}15`,
                            color: addin.colour
                        }}
                    >
                        <FaPuzzlePiece className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <Tooltip fixed text={addin.name} className="min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate">
                                    {addin.name}
                                </h3>
                            </Tooltip>
                            {addin.abbreviation && (
                                <span
                                    className="px-2 py-0.5 text-xs font-bold rounded-md flex-shrink-0"
                                    style={{
                                        backgroundColor: `${addin.colour}15`,
                                        color: addin.colour
                                    }}
                                >
                                    {addin.abbreviation}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                            {addin.size && (
                                <span className="px-2 py-1 rounded-md bg-gray-100 text-gray-600">
                                    {addin.size}
                                </span>
                            )}
                            {addin.category && (
                                <span className="px-2 py-1 rounded-md bg-gray-100 text-gray-500">
                                    {addin.category}
                                </span>
                            )}
                            {addin.taskCount !== undefined && (
                                <span className="text-gray-400">
                                    {addin.taskCount} task{addin.taskCount !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                            Attach to a main template to edit
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    // Helper to render the add-in templates pool section
    const renderPoolSection = () => (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FaPuzzlePiece className="w-4 h-4 text-gray-600" />
                        <h2 className="font-semibold text-gray-900">Add-in Templates</h2>
                        <span className="text-sm text-gray-500">({filteredPoolAddins.length})</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={poolSizeFilter}
                            onChange={(e) => setPoolSizeFilter(e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                            <option value="">All sizes</option>
                            {sizes.map(s => (
                                <option key={s.id} value={s.name}>{s.name}</option>
                            ))}
                        </select>
                        <select
                            value={poolCategoryFilter}
                            onChange={(e) => setPoolCategoryFilter(e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                            <option value="">All categories</option>
                            {poolCategories.map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            {poolLoading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent"></div>
                    <span className="ml-2 text-sm text-gray-500">Loading add-in templates...</span>
                </div>
            ) : filteredPoolAddins.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    {poolAddins.length === 0 ? 'No add-in templates yet' : 'No add-ins match the current filters'}
                </div>
            ) : (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPoolAddins.map(addin => renderPoolAddinCard(addin))}
                </div>
            )}
        </div>
    );

    return (
        <div className="h-[calc(100vh-64px)] overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Template Management</h1>
                        <p className="text-gray-500 text-sm mt-1">Manage your project templates and add-ins</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={openSizeModal}
                            className="flex items-center gap-2 px-4 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm transition-all text-sm font-medium"
                        >
                            <FaCog className="w-3.5 h-3.5" />
                            Manage Sizes
                        </button>
                        <button
                            onClick={() => {
                                setFormData({ name: '', description: '', type: 'main', abbreviation: '', colour: '#3B82F6', size: '', category: '' });
                                setShowCreateModal(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-600/20 transition-all hover:shadow-xl hover:shadow-primary-600/30 text-sm font-medium"
                        >
                            <FaPlus className="w-3.5 h-3.5" />
                            New Template
                        </button>
                    </div>
                </div>

                {/* Filter tabs */}
                <div className="flex gap-2 mb-6">
                    {['all', 'main', 'addin'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                                filter === f
                                    ? 'bg-white text-primary-700 shadow-md'
                                    : 'text-gray-600 hover:bg-white/50'
                            }`}
                        >
                            {f === 'all' ? 'All Templates' : f === 'main' ? 'Main Templates' : 'Add-in Templates'}
                        </button>
                    ))}
                </div>

                {/* Error display */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                        {error}
                    </div>
                )}

                {/* Template grid */}
                {filteredTemplates.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <FaLayerGroup className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates yet</h3>
                        <p className="text-gray-500 mb-4">Create your first template to get started</p>
                        <button
                            onClick={() => {
                                setFormData({ name: '', description: '', type: 'main', abbreviation: '', colour: '#3B82F6', size: '', category: '' });
                                setShowCreateModal(true);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                        >
                            <FaPlus className="w-3 h-3" />
                            Create Template
                        </button>
                    </div>
                ) : filter === 'all' ? (
                    // Split view - Main Templates on top, Add-in Templates on bottom
                    <div className="space-y-6">
                        {/* Main Templates Section */}
                        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                                <div className="flex items-center gap-2">
                                    <FaLayerGroup className="w-4 h-4 text-gray-600" />
                                    <h2 className="font-semibold text-gray-900">Main Templates</h2>
                                    <span className="text-sm text-gray-500">({mainTemplates.length})</span>
                                </div>
                            </div>
                            {mainTemplates.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">No main templates</div>
                            ) : (
                                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {mainTemplates.map(template => renderTemplateCard(template))}
                                </div>
                            )}
                        </div>

                        {/* Add-in Templates Section */}
                        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                                <div className="flex items-center gap-2">
                                    <FaPuzzlePiece className="w-4 h-4 text-gray-600" />
                                    <h2 className="font-semibold text-gray-900">Add-in Templates</h2>
                                    <span className="text-sm text-gray-500">({addinTemplates.length})</span>
                                </div>
                            </div>
                            {addinTemplates.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">No add-in templates</div>
                            ) : (
                                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {addinTemplates.map(template => renderTemplateCard(template))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredTemplates.map(template => renderTemplateCard(template))}
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {(showCreateModal || showEditModal) && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {showCreateModal ? 'Create Template' : 'Edit Template'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setShowEditModal(false);
                                    setSelectedTemplate(null);
                                    setError(null);
                                }}
                                className="p-2 hover:bg-gray-200 rounded-xl transition-colors"
                            >
                                <FaTimes className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>

                        <form onSubmit={showCreateModal ? handleCreate : handleUpdate} className="p-5 space-y-5">
                            {error && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
                                    placeholder="Enter template name"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
                                    placeholder="Brief description of this template"
                                    rows={3}
                                />
                            </div>

                            {showCreateModal && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Type *
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                            formData.type === 'main'
                                                ? 'border-gray-400 bg-gray-100'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}>
                                            <input
                                                type="radio"
                                                name="type"
                                                value="main"
                                                checked={formData.type === 'main'}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    type: e.target.value,
                                                    colour: '#3B82F6'
                                                })}
                                                className="sr-only"
                                            />
                                            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                                                <FaLayerGroup className="w-4 h-4 text-gray-600" />
                                            </div>
                                            <span className="font-medium text-gray-900">Main Template</span>
                                        </label>
                                        <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                            formData.type === 'addin'
                                                ? 'border-gray-400 bg-gray-100'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}>
                                            <input
                                                type="radio"
                                                name="type"
                                                value="addin"
                                                checked={formData.type === 'addin'}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    type: e.target.value,
                                                    colour: '#8B5CF6'
                                                })}
                                                className="sr-only"
                                            />
                                            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                                                <FaPuzzlePiece className="w-4 h-4 text-gray-600" />
                                            </div>
                                            <span className="font-medium text-gray-900">Add-in</span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {formData.type === 'addin' && (
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Abbreviation * (2-4 chars)
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.abbreviation}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                abbreviation: e.target.value.toUpperCase().substring(0, 4)
                                            })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase transition-shadow"
                                            placeholder="e.g. INT"
                                            maxLength={4}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Colour
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="color"
                                                value={formData.colour}
                                                onChange={(e) => setFormData({ ...formData, colour: e.target.value })}
                                                className="w-14 h-11 p-1 border border-gray-200 rounded-xl cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Size *
                                </label>
                                <select
                                    value={formData.size}
                                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
                                    required
                                >
                                    <option value="">Select a size</option>
                                    {sizes.map(s => (
                                        <option key={s.id} value={s.name}>{s.name}</option>
                                    ))}
                                </select>
                            </div>

                            {formData.type === 'addin' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
                                        placeholder="e.g. Integration Testing"
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-5 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setShowEditModal(false);
                                        setSelectedTemplate(null);
                                        setError(null);
                                    }}
                                    className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-xl font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-5 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg shadow-primary-600/20 transition-all flex items-center gap-2"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            Saving...
                                        </>
                                    ) : showCreateModal ? 'Create Template' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {/* Clone Modal */}
            {showCloneModal && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900">Clone Template</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Create a copy of "{showCloneModal.name}"
                            </p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={cloneName}
                                    onChange={(e) => setCloneName(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="Template name"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={cloneDescription}
                                    onChange={(e) => setCloneDescription(e.target.value)}
                                    rows={3}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                    placeholder="Template description"
                                />
                            </div>

                            {/* Add-in link selection for main templates */}
                            {showCloneModal.type === 'main' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Include these add-in links
                                    </label>
                                    {cloneLoadingProject ? (
                                        <div className="flex items-center justify-center py-4">
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent"></div>
                                            <span className="ml-2 text-sm text-gray-500">Loading add-ins...</span>
                                        </div>
                                    ) : cloneProjectData?.project?.addins?.length > 0 ? (
                                        <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-xl p-3">
                                            {cloneProjectData.project.addins.map(addin => {
                                                // Count tasks for this add-in
                                                const addinTasks = [
                                                    ...(cloneProjectData.tasks || []),
                                                    ...(cloneProjectData.subtasks || [])
                                                ].filter(t => t.sourceAddinId === addin.addinId);
                                                const taskCount = addinTasks.length;

                                                const isSelected = cloneSelectedAddins.includes(addin.addinId);

                                                return (
                                                    <label
                                                        key={addin.addinId}
                                                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                                            isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setCloneSelectedAddins([...cloneSelectedAddins, addin.addinId]);
                                                                } else {
                                                                    setCloneSelectedAddins(
                                                                        cloneSelectedAddins.filter(id => id !== addin.addinId)
                                                                    );
                                                                }
                                                            }}
                                                            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <div
                                                            className="w-3 h-3 rounded-sm flex-shrink-0"
                                                            style={{ backgroundColor: addin.colour }}
                                                        />
                                                        <span className="flex-1 text-sm text-gray-700">
                                                            {addin.name}
                                                            <span
                                                                className="ml-1 px-1.5 py-0.5 text-xs font-medium rounded"
                                                                style={{
                                                                    backgroundColor: `${addin.colour}15`,
                                                                    color: addin.colour
                                                                }}
                                                            >
                                                                {addin.abbreviation}
                                                            </span>
                                                        </span>
                                                        <span className="text-xs text-gray-400">
                                                            {taskCount} task{taskCount !== 1 ? 's' : ''}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400 py-2">No add-ins attached to this template</p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 p-4 bg-gray-50 border-t border-gray-100">
                            <button
                                onClick={() => {
                                    setShowCloneModal(null);
                                    setSelectedTemplate(null);
                                    setCloneName('');
                                    setCloneDescription('');
                                    setCloneProjectData(null);
                                    setCloneSelectedAddins([]);
                                }}
                                className="flex-1 px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                                disabled={cloning}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleClone}
                                disabled={!cloneName.trim() || cloning}
                                className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {cloning ? 'Cloning...' : 'Clone'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
                        <div className="p-6">
                            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <FaTrash className="w-5 h-5 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete Template?</h3>
                            <p className="text-gray-600 text-center text-sm">
                                Are you sure you want to delete "<span className="font-medium">{showDeleteConfirm.name}</span>"? This will also delete all associated tasks and cannot be undone.
                            </p>
                        </div>
                        <div className="flex gap-3 p-4 bg-gray-50 border-t border-gray-100">
                            <button
                                onClick={() => { setShowDeleteConfirm(null); setSelectedTemplate(null); }}
                                className="flex-1 px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Size Management Modal */}
            {showSizeModal && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50">
                            <h3 className="text-lg font-semibold text-gray-900">Manage Sizes</h3>
                            <button
                                onClick={() => setShowSizeModal(false)}
                                className="p-2 hover:bg-gray-200 rounded-xl transition-colors"
                            >
                                <FaTimes className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>
                        <div className="p-5">
                            <p className="text-sm text-gray-500 mb-4">
                                Define the size categories used by templates and add-ins. Sizes control which add-ins can attach to which templates.
                            </p>
                            <div className="space-y-2 mb-4">
                                {editedSizes.map((size, index) => (
                                    <div key={size.id} className="flex items-center gap-2">
                                        <div className="flex flex-col">
                                            <button
                                                type="button"
                                                onClick={() => moveSizeUp(index)}
                                                disabled={index === 0}
                                                className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                            >
                                                <FaArrowUp className="w-3 h-3" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveSizeDown(index)}
                                                disabled={index === editedSizes.length - 1}
                                                className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                            >
                                                <FaArrowDown className="w-3 h-3" />
                                            </button>
                                        </div>
                                        <input
                                            type="text"
                                            value={size.name}
                                            onChange={(e) => renameSize(size.id, e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                                            placeholder="Size name"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeSize(size.id)}
                                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                            <FaTrash className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={addSize}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                                <FaPlus className="w-3 h-3" />
                                Add Size
                            </button>
                        </div>
                        <div className="flex gap-3 p-4 bg-gray-50 border-t border-gray-100">
                            <button
                                onClick={() => setShowSizeModal(false)}
                                className="flex-1 px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                                disabled={sizesSaving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveSizes}
                                disabled={sizesSaving || editedSizes.some(s => !s.name.trim())}
                                className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {sizesSaving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        Saving...
                                    </>
                                ) : (
                                    'Save Sizes'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Templates;
